import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {AbortController | null} */
  #cartCountRequest = null;
  #historySyncRequested = false;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('pagehide', this.#cancelCartCountRequest);

    if (this.#isHistoryNavigation()) {
      // Covers modules that load after pageshow, without trusting an old count.
      this.#historySyncRequested = true;
      this.#refreshCartCount();
    } else {
      this.ensureCartBubbleIsCorrect();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('pagehide', this.#cancelCartCountRequest);
    this.#cancelCartCountRequest();
    this.#historySyncRequested = false;
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted || (this.#isHistoryNavigation() && !this.#historySyncRequested)) {
      this.#historySyncRequested = true;
      this.#refreshCartCount();
    }
  };

  #isHistoryNavigation() {
    const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    );
    return navigation?.type === 'back_forward';
  }

  #cancelCartCountRequest = () => {
    this.#cartCountRequest?.abort();
    this.#cartCountRequest = null;
  };

  async #refreshCartCount() {
    if (!this.isConnected || this.#cartCountRequest) return;
    const controller = new AbortController();
    this.#cartCountRequest = controller;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      const cart = await response.json();
      if (controller.signal.aborted || !this.isConnected) return;
      if (!Number.isSafeInteger(cart?.item_count) || cart.item_count < 0) {
        throw new Error('Invalid cart item count');
      }

      // Restore only the counter; this is not a new add-to-cart action.
      await this.renderCartBubble(cart.item_count, false);
    } catch (error) {
      if (!controller.signal.aborted && error?.name !== 'AbortError') {
        console.warn('[cart-icon] Could not refresh cart count:', error);
      }
    } finally {
      if (this.#cartCountRequest === controller) this.#cartCountRequest = null;
    }
  }

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    // A cart mutation takes priority over an older history-restoration request.
    this.#cancelCartCountRequest();
    event.promise
      ?.then(({ cart, detail }) => {
        this.#cancelCartCountRequest();
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(itemCount),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // The visible count still works when browser storage is unavailable.
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    try {
      const sessionStorageCount = sessionStorage.getItem('cart-count');

      // If no session storage data, nothing to check
      if (sessionStorageCount === null) return;

      const visibleCount = this.refs.cartBubbleCount.textContent;
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}